import React, { useEffect, useMemo, useRef } from 'react';
import { envVariables } from '@fabric-ui/utils';
// import { Link as MuiLink, LinkProps as MuiLinkProps } from '@mui/material';
import {
  createFileRoute,
  createLazyFileRoute,
  createRootRoute,
  HistoryState,
  NavigateOptions,
  Link as RouterLink,
  useLocation as TanstackLocation,
  useNavigate as TanstackNavigate,
  useBlocker,
  useParams as useParamsTanstack,
  useRouterState,
  useSearch as useSearchTanstack,
} from '@tanstack/react-router';

const getLocationState = <T = HistoryState,>(state?: Record<string, unknown>): T => {
  if (!state) return {} as T;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { __hashScrollIntoViewOptions, key, __TSR_index, __TSR_key, ...rest } = state;
  return Object.keys(rest).length ? (rest as T) : ({} as T);
};

const useLocation = (options = {}) => {
  const location = TanstackLocation({ ...options, structuralSharing: true });
  return useMemo(() => {
    const cleanedState = getLocationState(location.state);
    return { ...location, state: cleanedState };
  }, [location]);
};

const useSearchParams = (defaultParams?): [Record<string, unknown>, any] => {
  const defaultSearchParamsRef = React.useRef(defaultParams);
  const hasSetSearchParamsRef = React.useRef(false);
  const location = useLocation({ select: location => ({ search: location.search, searchStr: location.searchStr }) });
  const search = React.useMemo(
    () => getSearchParamsForLocation(location.search, hasSetSearchParamsRef.current ? null : defaultSearchParamsRef.current),
    [location?.searchStr]
  );
  const navigate = useNavigate();
  const setSearch = React.useCallback(
    (nextInit, options: NavigateOptions) => {
      const newSearchParams = typeof nextInit === 'function' ? nextInit(search) : nextInit;
      navigate({ search: newSearchParams, ...options });
    },
    [search]
  );

  useEffect(() => {
    hasSetSearchParamsRef.current = true;
    return () => {
      hasSetSearchParamsRef.current = false;
    };
  }, []);

  return [search, setSearch];
};
const useParams = (options = {}) => useParamsTanstack({ strict: false, ...options });

const getSearchParamsForLocation = (locationSearch: Record<string, unknown>, defaultSearchParams: Record<string, unknown> | null) => {
  const searchParams = locationSearch;
  if (defaultSearchParams && typeof defaultSearchParams === 'object') {
    // Use `defaultSearchParams.forEach(...)` here instead of iterating of
    // `defaultSearchParams.keys()` to work-around a bug in Firefox related to
    // web extensions. Relevant Bugzilla tickets:
    // https://bugzilla.mozilla.org/show_bug.cgi?id=1414602
    // https://bugzilla.mozilla.org/show_bug.cgi?id=1023984
    // @ts-ignore
    Object.entries(defaultSearchParams).forEach(entry => {
      const key = entry[0];
      // @ts-ignore
      if (!(key in searchParams)) {
        // @ts-ignore
        searchParams[key] = defaultSearchParams[key];
      }
    });
  }

  return searchParams;
};

const useNavigate = () => {
  const navigate = TanstackNavigate();
  return useMemo(
    () => opt => {
      if (typeof opt === 'string') {
        try {
          // @ts-expect-error: `from` expects a registered route path literal but we pass a runtime string
          return navigate({ from: window.location.pathname, to: opt });
        } catch {
          return navigate({ to: opt });
        }
      }
      if (opt.replace) {
        return navigate(opt);
      }
      try {
        return navigate({ from: window.location.pathname, ...opt });
      } catch {
        return navigate(opt);
      }
    },
    [navigate]
  );
};

const useSearch = options =>
  useSearchTanstack({ strict: false, structuralSharing: true, ...options }) as ReturnType<typeof useSearchTanstack>;

const useIsIdComponent = () => window.location.pathname.split('/').at(-1) === '$id';

const Link = RouterLink as any;
// const MuiLinkk: any | LinkComponent<typeof MUILinkComponent> = createLink(MUILinkComponent);

const shouldShowDevTools = localStorage.getItem('avalor-devtools') === 'true';

const TanStackRouterDevtools =
  envVariables.MODE === 'production'
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import('@tanstack/router-devtools').then(res => ({
          default: res.TanStackRouterDevtools,
          // For Embedded Mode
          // default: res.TanStackRouterDevtoolsPanel
        }))
      );

const useOnSubNavigate = (callback: (isNavigatingToSubPath: boolean) => void, enabled = true) => {
  const routerState = useRouterState();
  const initialMatches = useRef(routerState.matches);

  useEffect(() => {
    if (enabled) {
      if (!routerState.pendingMatches) {
        return;
      }
      const initialMatchesIds = initialMatches.current.map(match => match.routeId.replaceAll('/', ''));
      const nextMatches = routerState.pendingMatches.map(match => match.routeId.replaceAll('/', ''));

      callback(nextMatches.length >= initialMatchesIds.length && initialMatchesIds.every(initialId => nextMatches.includes(initialId)));
    }
  }, [routerState.location.href, enabled]);

  return () => (initialMatches.current = routerState.matches);
};

export {
  createFileRoute,
  createLazyFileRoute,
  createRootRoute,
  useNavigate,
  useLocation,
  useSearchParams,
  Link,
  useBlocker,
  useParams,
  useSearch,
  useIsIdComponent,
  TanStackRouterDevtools,
  shouldShowDevTools,
  useOnSubNavigate,
  getLocationState,
};
